import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Spinner } from 'frosted-ui';
import { api } from '../services/api';
import OfferPage from './OfferPage';
import DeadLinkNotice from '../components/DeadLinkNotice';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import type { PublicOffer, SellerInfo } from '../types';

// Incoming link params forwarded to the router, which validates them and
// carries them into the Whop checkout URL (affiliate `a`, UTM) or records
// them as the visit's source (`ref`).
const PASSTHROUGH_PARAMS = [
  'a',
  'ref',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

function pickPassthrough(searchParams: URLSearchParams): Record<string, string> {
  const params: Record<string, string> = {};
  for (const key of PASSTHROUGH_PARAMS) {
    const value = searchParams.get(key);
    if (value) params[key] = value;
  }
  return params;
}

export default function BuyPage() {
  const { experimentId, companyRoute, shareCode } = useParams<{
    experimentId?: string;
    companyRoute?: string;
    shareCode?: string;
  }>();
  const [searchParams] = useSearchParams();
  // Read once: the offer page rewrites the query (?payment=, ?restore=) and
  // must not trigger a second resolve (and a second visit).
  const initialSearch = useRef(searchParams);
  const [error, setError] = useState<string | null>(null);
  const [offer, setOffer] = useState<PublicOffer | null>(null);
  const [showSpinner, setShowSpinner] = useState(false);
  // A dead /g/ link still names its seller when we know the store route.
  const [seller, setSeller] = useState<SellerInfo | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setShowSpinner(true), 200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const assignVariantAndRedirect = async () => {
      try {
        const search = initialSearch.current;
        const passthrough = pickPassthrough(search);

        let assignment: { purchaseUrl: string; offer?: PublicOffer };

        if (companyRoute && shareCode) {
          // Short share-friendly URL: /g/:companyRoute/:shareCode
          assignment = await api.getVariantAssignmentByShareCode(
            companyRoute,
            shareCode,
            passthrough
          );
        } else if (experimentId) {
          // Legacy signed URL: /buy/:experimentId?signature=...&expires=...
          const signature = search.get('signature');
          const expires = search.get('expires');

          if (!signature || !expires) {
            throw new Error('Invalid purchase link - missing required parameters');
          }

          assignment = await api.getVariantAssignment(
            experimentId,
            signature,
            expires,
            passthrough
          );
        } else {
          throw new Error('Invalid purchase link');
        }

        // Unlock tests sell on our hosted offer page, not a Whop redirect.
        if (assignment.offer) {
          setOffer(assignment.offer);
          return;
        }
        window.location.href = assignment.purchaseUrl;
      } catch (err: unknown) {
        console.error('Error assigning variant:', err);
        setError((err instanceof Error && err.message) || 'Failed to process purchase link');
        if (companyRoute) {
          api
            .getSeller(companyRoute)
            .then(setSeller)
            .catch(() => {
              // Unknown store: the generic message stands.
            });
        }
      }
    };

    assignVariantAndRedirect();
  }, [experimentId, companyRoute, shareCode]);

  if (offer) return <OfferPage initialOffer={offer} />;
  return <BuyLinkStatus error={error} showSpinner={showSpinner} seller={seller} />;
}

/** Redirecting (spinner), or the dead-link page naming the seller when known. */
function BuyLinkStatus({
  error,
  showSpinner,
  seller,
}: {
  error: string | null;
  showSpinner: boolean;
  seller: SellerInfo | null;
}) {
  useDocumentTitle(error ? seller?.sellerName || 'Link not found' : 'Checkout');
  if (error) return <DeadLinkNotice seller={seller} />;
  if (!showSpinner) return null;
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Spinner size="2" />
    </div>
  );
}
